import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { entryPoint } from '../entry';
import { parseRoute, routeToPath } from './routes';
import type { Route } from './routes';

/**
 * A router for a closed set of addresses.
 *
 * Small on purpose: history, links and a typed route union is all this console
 * needs, and a typed union is what keeps a tenancy id out of the tenancy
 * screens and each entry point's addresses out of the other's (see routes.ts).
 * If nested layouts or lazy routes ever arrive, this is one module to replace.
 *
 * The entry point is read here once, from the module that resolved it from the
 * host. It is not state, it cannot change while the page is open, and nothing
 * in the tree can ask for a different one.
 */

interface RouterState {
  route: Route;
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterState | null>(null);

export function Router({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>(() => parseRoute(entryPoint(), window.location));

  useEffect(() => {
    const onPop = () => {
      setRoute(parseRoute(entryPoint(), window.location));
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  const navigate = useCallback((next: Route, options?: { replace?: boolean }) => {
    const path = routeToPath(next);
    if (options?.replace === true) {
      window.history.replaceState(null, '', path);
    } else {
      window.history.pushState(null, '', path);
    }
    setRoute(next);
  }, []);

  const value = useMemo<RouterState>(() => ({ route, navigate }), [route, navigate]);

  return <RouterContext value={value}>{children}</RouterContext>;
}

export function useRouter(): RouterState {
  const value = useContext(RouterContext);
  if (value === null) {
    throw new Error('useRouter() used outside <Router>');
  }
  return value;
}

type LinkProps = { to: Route; children: ReactNode } & Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
>;

/**
 * A real `<a href>`, so middle-click, ctrl-click and the screen reader's link
 * list all keep working. Only a plain left-click is handled in the app.
 */
export function Link({ to, children, onClick, ...rest }: LinkProps) {
  const { navigate } = useRouter();

  return (
    <a
      href={routeToPath(to)}
      onClick={(event) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
